import { getUnixTimeSeconds } from '$Components/Shared/DateFormatter';
import { WidgetTooltip } from '$Components/Shared/WidgetTooltip';
import { SpeedGraphPointResponse, VideoEventTypePairingResponse } from '$Generated/api';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { React, unit } from 'Imports';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

const styles = require('./SpeedGraph.scss') as {
    container: string;
    eventDot: string;
    tooltipOverlay: string;
    tooltipPrompt: string;
    tooltipEvent: string;
};

interface SpeedGraphProps {
    data: SpeedGraphPointResponse[];
    videoEventTypePairings: VideoEventTypePairingResponse[];
    eventStartDate: Date;
}

const EVENT_DOT_COLOR = '#E36822';

interface SpeedChartPoint {
    time: number;
    speed: number;
    isEvent: boolean;
    eventTypes?: string[];
}

const interpolateSpeed = (eventTime: number, speedPoints: SpeedChartPoint[]): number => {
    if (speedPoints.length === 0) return 0;
    if (eventTime <= speedPoints[0].time) return speedPoints[0].speed;
    if (eventTime >= speedPoints[speedPoints.length - 1].time) return speedPoints[speedPoints.length - 1].speed;

    const rightIndex = speedPoints.findIndex((p) => p.time >= eventTime);
    const p1 = speedPoints[rightIndex - 1];
    const p2 = speedPoints[rightIndex];

    return p1.speed + ((p2.speed - p1.speed) * (eventTime - p1.time)) / (p2.time - p1.time);
};

interface ActiveTooltip {
    x: number;
    y: number;
    eventTypes: string[];
}

const TooltipOnClick = ({ children }: { children: React.ReactNode }) => {
    const ref = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        (ref.current?.firstElementChild as HTMLElement)?.click();
    }, []);
    return <div ref={ref}>{children}</div>;
};

const EventDot = ({ cx, cy, payload, onDotClick }: any) => {
    if (!payload?.isEvent || cx === undefined || cy === undefined) return null;
    return (
        <circle
            cx={cx}
            cy={cy}
            r={4}
            fill={EVENT_DOT_COLOR}
            stroke={EVENT_DOT_COLOR}
            strokeWidth={1}
            className={styles.eventDot}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
            onClick={(e: React.MouseEvent<SVGCircleElement>) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                onDotClick(rect.left + rect.width / 2, rect.top + rect.height / 2, payload.eventTypes);
            }}
        />
    );
};

const _SpeedGraph = ({
    data,
    videoEventTypePairings,
    eventStartDate,
    integrationPartnerData,
}: SpeedGraphProps & IIntegrationPartnerDataInjectedProps) => {
    const isMetric = integrationPartnerData.getUserIsMetric();
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(null);

    useEffect(() => {
        if (!activeTooltip) return;
        const handleClickAway = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setActiveTooltip(null);
            }
        };
        document.addEventListener('mousedown', handleClickAway);
        return () => document.removeEventListener('mousedown', handleClickAway);
    }, [activeTooltip]);

    useEffect(() => {
        const handleResize = () => setActiveTooltip(null);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDotClick = useCallback((clientX: number, clientY: number, eventTypes: string[]) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        setActiveTooltip((prev) => (prev !== null && Math.abs(prev.x - x) < 5 ? null : { x, y, eventTypes }));
    }, []);

    const baseTimestamp = useMemo(() => {
        const speedTimes = data.filter((point) => !!point.time).map((point) => getUnixTimeSeconds(point.time!));
        const eventTimes = videoEventTypePairings.filter((ev) => !!ev.startDate).map((ev) => getUnixTimeSeconds(ev.startDate!));
        const allTimes = [...speedTimes, ...eventTimes];
        return allTimes.length > 0 ? Math.min(...allTimes) : 0;
    }, [data, videoEventTypePairings]);

    const xDomainEnd = useMemo(() => {
        const speedTimes = data.filter((point) => !!point.time).map((point) => getUnixTimeSeconds(point.time!) - baseTimestamp);
        const eventTimes = videoEventTypePairings
            .filter((ev) => !!ev.startDate)
            .map((ev) => getUnixTimeSeconds(ev.startDate!) - baseTimestamp);
        return Math.max(...speedTimes, ...eventTimes, 0);
    }, [data, videoEventTypePairings, baseTimestamp]);

    const speedChartPoints = useMemo(() => {
        return data
            .filter((point) => !!point.time)
            .map((point) => ({
                time: getUnixTimeSeconds(point.time!) - baseTimestamp,
                speed: unit.getSpeedValue(point.speed, isMetric),
                isEvent: false,
            }));
    }, [data, baseTimestamp, isMetric]);

    const chartData = useMemo(() => {
        const coalesceTimeThreshold = 0.1;
        const eventPoints: SpeedChartPoint[] = [];
        let previousEventTime: number | undefined;
        let coalescedTimeSum = 0;
        let coalescedEventCount = 0;

        const orderedEvents = [...videoEventTypePairings].sort((a, b) => {
            if (!a.startDate || !b.startDate) return 0;
            return a.startDate > b.startDate ? 1 : a.startDate < b.startDate ? -1 : 0;
        });

        orderedEvents.forEach((event) => {
            const subeventDate = event.startDate || (videoEventTypePairings.length === 1 ? eventStartDate : undefined);
            if (!subeventDate) return;

            const eventTime = getUnixTimeSeconds(subeventDate) - baseTimestamp;
            const eventType = event.videoEventTypeString || 'Default';

            if (previousEventTime !== undefined && eventTime - previousEventTime <= coalesceTimeThreshold) {
                coalescedTimeSum += eventTime;
                coalescedEventCount += 1;
                const avgTime = coalescedTimeSum / coalescedEventCount;
                const last = eventPoints[eventPoints.length - 1];
                last.time = avgTime;
                last.speed = interpolateSpeed(avgTime, speedChartPoints);
                if (!last.eventTypes!.includes(eventType)) {
                    last.eventTypes!.push(eventType);
                }
                return;
            }

            coalescedTimeSum = eventTime;
            coalescedEventCount = 1;
            previousEventTime = eventTime;

            eventPoints.push({
                time: eventTime,
                speed: interpolateSpeed(eventTime, speedChartPoints),
                isEvent: true,
                eventTypes: [eventType],
            });
        });

        return [...speedChartPoints, ...eventPoints].sort((a, b) => a.time - b.time);
    }, [speedChartPoints, videoEventTypePairings, eventStartDate, baseTimestamp]);

    const xAxisTicks = useMemo(() => {
        const tickSteps = [1, 2, 5, 10, 15, 30, 60];
        const targetTicks = 20;
        const step = tickSteps.find((s) => s >= xDomainEnd / targetTicks) ?? 60;
        return Array.from({ length: Math.floor(xDomainEnd / step) + 1 }, (_, i) => i * step);
    }, [xDomainEnd]);

    const yAxisTicks = useMemo(() => {
        const speeds = speedChartPoints.map((p) => p.speed);
        if (speeds.length === 0) return [0, 5, 10];

        const min = Math.min(...speeds);
        const max = Math.max(...speeds);
        const range = min === max ? 10 : max - min;

        const tickSteps = [1, 2, 5, 10, 20, 25, 50];
        const targetTicks = 5;
        const step = tickSteps.find((s) => s >= range / targetTicks) ?? 50;

        const start = Math.floor(min / step) * step;
        const end = Math.ceil(max / step) * step;

        return Array.from({ length: Math.round((end - start) / step) + 1 }, (_, i) => Math.round(start + i * step));
    }, [speedChartPoints]);

    return (
        <div ref={containerRef} className={styles.container} onClick={() => setActiveTooltip(null)}>
            <LineChart width={800} height={140} data={chartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                    dataKey="time"
                    domain={[0, xDomainEnd]}
                    type="number"
                    ticks={xAxisTicks}
                    tickFormatter={(v) => Math.round(v).toString()}
                    label={{ value: 'seconds', position: 'insideBottom', offset: -5, fontSize: '12px', fill: '#202328' }}
                    tick={{ fontSize: '12px', fill: '#202328' }}
                />
                <YAxis
                    ticks={yAxisTicks}
                    domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
                    tickFormatter={(v) => Math.round(v).toString()}
                    label={{
                        value: isMetric ? 'kph' : 'mph',
                        angle: -90,
                        position: 'insideLeft',
                        fontSize: '12px',

                        style: { textAnchor: 'middle', fill: '#202328' },
                        offset: Math.max(...yAxisTicks).toString().length >= 3 ? 20 : 25,
                    }}
                    tick={{ fontSize: '12px', fill: '#202328' }}
                />
                <Line
                    type="monotoneX"
                    dataKey="speed"
                    stroke="#0078D3"
                    strokeWidth={2}
                    isAnimationActive={false}
                    dot={<EventDot onDotClick={handleDotClick} />}
                    activeDot={false}
                />
            </LineChart>
            {activeTooltip && (
                <div
                    className={styles.tooltipOverlay}
                    style={{ left: activeTooltip.x - 4, top: activeTooltip.y + 3 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <TooltipOnClick>
                        <WidgetTooltip
                            prompt={<span className={styles.tooltipPrompt} />}
                            description={
                                <span>
                                    {[...activeTooltip.eventTypes].sort().map((type, i) => (
                                        <span key={i} className={styles.tooltipEvent}>
                                            {type}
                                        </span>
                                    ))}
                                </span>
                            }
                            contentPosition="topCenter"
                            hoverTooltipDisabled={true}
                        />
                    </TooltipOnClick>
                </div>
            )}
        </div>
    );
};

const SpeedGraph = IntegrationPartnerDataService.inject(_SpeedGraph);

export default SpeedGraph;
